/**
 * Stock status (R17) — the mirror of `private.stock_status`
 * (migration 20260923000001). `v_items_list.stock_status` is the authority; this
 * copy exists so a row edited in the browser can re-chip itself before the view
 * is re-read, and so the mobile item card can colour an offline figure.
 */
import { D, type DecimalInput } from '../money';

export type StockStatus = 'normal' | 'low' | 'critical' | 'negative';

/**
 * `settings.stock.critical_ratio`. **[[PLACEHOLDER]] E6** — the owner has not
 * confirmed it; 0,5 is what the seed writes and what the SQL falls back to.
 */
export const DEFAULT_CRITICAL_RATIO = '0.5';

/**
 * `critical_ratio` is stored as a ratio (0,5) and drawn as a percentage
 * (W-31b "Kritik stok eşiği · 50 %"), so the settings row converts both ways
 * through Decimal — 0,07 × 100 must read 7, never 7.000000000000001.
 */
export function ratioToPercent(ratio: DecimalInput): string {
  return D(ratio).times(100).toString();
}

export function percentToRatio(percent: DecimalInput): string {
  return D(percent).dividedBy(100).toString();
}

export interface StockStatusOptions {
  /** `settings.stock.critical_ratio`; missing means the seeded 0,5. */
  criticalRatio?: DecimalInput;
  /**
   * True when any single balance behind this figure is negative. A total can be
   * positive while one location is short, and that still reads "negatif".
   */
  anyNegative?: boolean;
}

/**
 * Own-warehouse quantity + reorder point → the chip W-04, W-06 and M-03 draw.
 * An item without a reorder point is `normal` unless something is negative.
 */
export function stockStatus(onHand: DecimalInput, reorderPoint: DecimalInput, opts: StockStatusOptions = {}): StockStatus {
  const qty = D(onHand);
  if (opts.anyNegative || qty.lessThan(0)) return 'negative';
  const point = D(reorderPoint);
  if (point.lessThanOrEqualTo(0)) return 'normal';
  const ratio = opts.criticalRatio === undefined || opts.criticalRatio === null ? D(DEFAULT_CRITICAL_RATIO) : D(opts.criticalRatio);
  if (qty.lessThanOrEqualTo(point.times(ratio))) return 'critical';
  if (qty.lessThanOrEqualTo(point)) return 'low';
  return 'normal';
}
